/**
 * Visual stub for the Reviews & Ratings module. The backend reviews
 * service has not been implemented yet, so this generates deterministic
 * mock data from an entity's ID purely for front-end display purposes.
 */

export interface Review {
  id: string;
  authorName: string;
  rating: number;
  comment: string;
  date: string;
}

export interface RatingSummary {
  averageRating: number;
  totalReviews: number;
  reviews: Review[];
}

const MOCK_AUTHORS = [
  'James O.',
  'Priya S.',
  'Daniel K.',
  'Megan T.',
  'Chidi A.',
  'Sofia R.',
];

const MOCK_COMMENTS = [
  'Great experience, will definitely book again!',
  'Friendly staff and a really clean, modern space.',
  'Best fade I’ve had in years.',
  'Easy to book online and the service was excellent.',
  'Good value for money, highly recommend.',
  'Professional and punctual - exactly on time.',
];

/** Deterministically derives a pseudo-random seed from an entity ID. */
const seedFromId = (id: bigint | string): number => {
  const str = id.toString();
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) % 100000;
  }
  return hash;
};

/**
 * Returns a stable mock rating summary (average rating + sample reviews)
 * for a given shop/barber ID. Remove once the reviews backend is live.
 */
export const getMockRatingSummary = (id: bigint | string): RatingSummary => {
  const seed = seedFromId(id);
  const totalReviews = 3 + (seed % 10);
  const averageRating = 5;

  const reviews: Review[] = Array.from({ length: totalReviews }, (_, i) => {
    const authorIndex = (seed + i) % MOCK_AUTHORS.length;
    const commentIndex = (seed + i * 2) % MOCK_COMMENTS.length;
    const rating = 5;
    const daysAgo = 3 + i * 9 + (seed % 10);
    const date = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);

    return {
      id: `${id.toString()}-review-${i}`,
      authorName: MOCK_AUTHORS[authorIndex],
      rating,
      comment: MOCK_COMMENTS[commentIndex],
      date,
    };
  });

  return { averageRating, totalReviews, reviews };
};
